import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {
  Container,
  DrawTool,
  EditNodeTool,
  EditTableTool,
  Focus,
  HighlightTool, PointerTool,
  SelectTool
} from '@app/modules/svg/public-api';
import {MessageRecord} from '@app/services/data.service';
import {EditSegmentTool} from '@app/modules/svg/service/tool/edit/cartesian/EditSegmentTool';


/**
 * The names of the events which should ignored during undo and redo operations.
 */
const TRANSPARENT_EVENT_TYPES: string[] = ['RECENTER_REF_POINT'];

export class LocalStack {
  public events: MessageRecord[] = [];
  public pointer = 0;

  public addEvent(event: MessageRecord): void {
    if (this.events.length > this.pointer) {
      if (TRANSPARENT_EVENT_TYPES.includes(event.name)) {
        return;
      }
      this.events = this.events.slice(0, this.pointer);
    }
    this.events.push(event);
    this.pointer = this.events.length;
  }
  public clear(): void {
    this.events = [];
    this.pointer = 0;
  }

  public undoPointer(): void {
    /** If the last event is transparent, we need to go back one more step to get the previous non-transparent event. */
    if (this.pointer === this.events.length) {
      while (TRANSPARENT_EVENT_TYPES.includes(this.events[this.pointer-1]?.name)) {
        this.pointer--;
      }
    }

    do {
      this.pointer--;
    } while (this.pointer > 0 && TRANSPARENT_EVENT_TYPES.includes(this.events[this.pointer-1]?.name));
  }

  public redoPointer(): void {
    do {
      this.pointer++;
    } while (this.pointer < this.events.length && TRANSPARENT_EVENT_TYPES.includes(this.events[this.pointer-1]?.name));
  }
}

export interface Whiteboard {
  container: Container;
  width: number;
  height: number;
  lastPostedProblemId: number;
  wrapper: HTMLDivElement;
  scrollPosition: number;
  broadcastScroll: boolean;
  localStack: LocalStack;
  streaming: boolean;
  smooth: boolean;
  sharedUsers: Set<number>; /* empty set means shared with all. null means not shared */
  tools: {
    drawTools: {[key: number]: DrawTool};
    selectTools: {[key: number]: SelectTool};
    editSegmentTools: {[key: number]: EditSegmentTool};
    editNodeTools: {[key: number]: EditNodeTool};
    editTableTools: {[key: number]: EditTableTool};
    studentPointerTools: {[key: number]: PointerTool};
    highlighterTools: {[key: number]: HighlightTool};
  };
  staffPointerTool: PointerTool;
  staffPointersCount: number;
  focuses: {[key: number]: Focus};
}

export namespace WhiteboardAction {
  export class Reset {
    static readonly type = '[Whiteboard] reset';

    constructor() {}
  }
  export class SetContainer {
    static readonly type = '[Whiteboard] set container';

    constructor(public container: Container) {}
  }
  export class SetWrapper {
    static readonly type = '[Whiteboard] set wrapper element';

    constructor(public wrapper: HTMLDivElement) {}
  }
  export class SetStaffPointerTool {
    static readonly type = '[Whiteboard] set staff pointer tool';

    constructor(public pointerTool: PointerTool) {}
  }
  export class SetStaffPointersCount {
    static readonly type = '[Whiteboard] set staff pointer tools count';

    constructor(public count: number) {}
  }
  export class SetScrollPosition {
    static readonly type = '[Whiteboard] set scroll position';

    constructor(public scrollPosition: number) {}
  }
  export class SetBroadcastScroll {
    static readonly type = '[Whiteboard] set broadcast scroll';

    constructor(public broadcastScroll: boolean) {}
  }
  export class SetHeight {
    static readonly type = '[Whiteboard] set height';

    constructor(public height: number) {}
  }
  export class SetWidth {
    static readonly type = '[Whiteboard] set width';

    constructor(public width: number) {}
  }
  export class SetLastPostedProblemId {
    static readonly type = '[Whiteboard] set last posted problem id';

    constructor(public id: number) {}
  }
  export class SetStreaming {
    static readonly type = '[Whiteboard] set streaming';

    constructor(public streaming: boolean) {}
  }
  export class SetSmooth {
    static readonly type = '[Whiteboard] set smooth drawing';

    constructor(public smooth: boolean) {}
  }

  export class ShareWith {
    static readonly type = '[Whiteboard] share whiteboard with student';
    constructor(public userId: number) {}
  }
  export class ShareWithAll {
    static readonly type = '[Whiteboard] share whiteboard with all students';
    constructor() {}
  }
  export class UnshareWith {
    static readonly type = '[Whiteboard] unshare whiteboard with student';
    constructor(public userId: number) {}
  }
  export class UnshareWithAll {
    static readonly type = '[Whiteboard] unshare whiteboard with all students';
    constructor() {}
  }
}

@State<Whiteboard>({
  name: WhiteboardState.storeName,
  defaults: WhiteboardState.defaults
})
@Injectable()
export class WhiteboardState {
  public static readonly VISIBLE_HEIGHT = 552;
  public static readonly VISIBLE_WIDTH = 1117;
  public static readonly MAX_HEIGHT = WhiteboardState.VISIBLE_HEIGHT * 24;

  static readonly storeName: string = 'whiteboard';
  static readonly defaults: Whiteboard = {
    container: undefined,
    height: WhiteboardState.VISIBLE_HEIGHT,
    width: WhiteboardState.VISIBLE_WIDTH,
    lastPostedProblemId: undefined,
    wrapper: undefined,
    scrollPosition: 0,
    broadcastScroll: true,
    localStack: new LocalStack(),
    streaming: false,
    smooth: false,
    sharedUsers: null,
    tools: {
      drawTools: {},
      selectTools: {},
      editNodeTools: {},
      editSegmentTools: {},
      editTableTools: {},
      studentPointerTools: {},
      highlighterTools: {}
    },
    staffPointerTool: undefined,
    staffPointersCount: 0,
    focuses: {}
  };

  @Selector()
  static getWhiteboard(whiteboard: Whiteboard): Whiteboard {
    return whiteboard;
  }

  @Action(WhiteboardAction.Reset)
  reset({setState}: StateContext<Whiteboard>, {}: WhiteboardAction.Reset) {
    const defaults: Whiteboard = JSON.parse(JSON.stringify(WhiteboardState.defaults));
    defaults.localStack = new LocalStack();

    setState(defaults);
  }

  @Action(WhiteboardAction.SetContainer)
  setContainer({patchState}: StateContext<Whiteboard>, {container}: WhiteboardAction.SetContainer) {
    patchState({container});
  }

  @Action(WhiteboardAction.SetWrapper)
  setWrapper({patchState}: StateContext<Whiteboard>, {wrapper}: WhiteboardAction.SetWrapper) {
    patchState({wrapper});
  }

  @Action(WhiteboardAction.SetBroadcastScroll)
  setBroadcastScroll({patchState}: StateContext<Whiteboard>, {broadcastScroll}: WhiteboardAction.SetBroadcastScroll) {
    patchState({broadcastScroll});
  }

  @Action(WhiteboardAction.SetHeight)
  setHeight({patchState}: StateContext<Whiteboard>, {height}: WhiteboardAction.SetHeight) {
    patchState({height});
  }

  @Action(WhiteboardAction.SetWidth)
  setWidth({patchState}: StateContext<Whiteboard>, {width}: WhiteboardAction.SetWidth) {
    patchState({width});
  }

  @Action(WhiteboardAction.SetLastPostedProblemId)
  setLastPostedProblemId({patchState}: StateContext<Whiteboard>, {id}: WhiteboardAction.SetLastPostedProblemId) {
    patchState({lastPostedProblemId: id});
  }

  @Action(WhiteboardAction.SetScrollPosition)
  setScrollPosition({patchState}: StateContext<Whiteboard>, {scrollPosition}: WhiteboardAction.SetScrollPosition) {
    patchState({scrollPosition});
  }

  @Action(WhiteboardAction.SetStaffPointerTool)
  setStaffPointerTool({patchState}: StateContext<Whiteboard>, {pointerTool}: WhiteboardAction.SetStaffPointerTool) {
    patchState({staffPointerTool: pointerTool});
  }

  @Action(WhiteboardAction.SetStaffPointersCount)
  setStaffPointerCount({patchState}: StateContext<Whiteboard>, {count}: WhiteboardAction.SetStaffPointersCount) {
    patchState({staffPointersCount: count});
  }

  @Action(WhiteboardAction.SetStreaming)
  setStreaming({patchState}: StateContext<Whiteboard>, {streaming}: WhiteboardAction.SetStreaming) {
    patchState({streaming});
  }

  @Action(WhiteboardAction.SetSmooth)
  setSmooth({patchState}: StateContext<Whiteboard>, {smooth}: WhiteboardAction.SetSmooth) {
    patchState({smooth});
  }

  @Action(WhiteboardAction.ShareWith)
  shareWith({getState, patchState}: StateContext<Whiteboard>, {userId}: WhiteboardAction.ShareWith) {
    const whiteboard: Whiteboard = getState();

    if (!whiteboard.sharedUsers) {
      whiteboard.sharedUsers = new Set<number>();
    } else if (whiteboard.sharedUsers.size === 0) { /* shared with all, and can't be shared individually */
      return;
    }

    whiteboard.sharedUsers.add(userId);
    patchState({...whiteboard});
  }

  @Action(WhiteboardAction.ShareWithAll)
  shareWithAll({patchState}: StateContext<Whiteboard>) {
    patchState({sharedUsers: new Set<number>()});
  }

  @Action(WhiteboardAction.UnshareWith)
  unshareWith({getState, patchState}: StateContext<Whiteboard>, {userId}: WhiteboardAction.UnshareWith) {
    const whiteboard: Whiteboard = getState();

    if (whiteboard.sharedUsers) {
      whiteboard.sharedUsers.delete(userId);
      if (whiteboard.sharedUsers.size === 0) { /* if unshared with all, unshare whiteboard */
        whiteboard.sharedUsers = null;
      }
      patchState({...whiteboard});
    }
  }

  @Action(WhiteboardAction.UnshareWithAll)
  unshareWithAll({patchState}: StateContext<Whiteboard>) {
    patchState({sharedUsers: null});
  }
}
